<template>
  <div>
    <v-dialog
        v-model="dialog"
        fullscreen
        persistent
        scrollable
        attach="body"
        content-class="section-panel-dialog"
        transition="fade-transition"
        @keyup.esc="closeDialog"
    >
      <v-card class="section-panel-host rounded-0">
        <Transition name="slide-panel" appear @after-leave="afterPanelLeave">
          <div v-if="panelVisible" class="local-panel">
            <v-card class="local-panel-card rounded-0 py-0">
              <v-card-text class="local-panel-content pa-0">
                <component :is="section.component" ref="SECTION_DIALOG" v-bind="currentProperties" v-on="events"/>
              </v-card-text>
            </v-card>
          </div>
        </Transition>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import Section from '@/framework/base/classes/Section';
import Route from '@/generated/classes/Route';
import { FieldStatus } from '@/framework/base/components/FieldInfo';
import { MapRessource } from '@/framework/base/helpers/Ressources';
import { type PropType, defineComponent } from 'vue';

export default defineComponent({
  name: 'SectionContainerPanel',

  components: {},

  props: {
    section: {
      type: Section,
      required: true,
    },

    dialogOpen: {
      type: Boolean,
      required: false,
    },

    sectionSave: {
      type: Boolean,
      required: false,
    },

    helper: {
      type: Boolean,
      required: false,
    },

    moduleId: {
      type: Number,
      required: false,
    },

    fullscreen: {
      type: Boolean,
      required: false,
    },

    hideHeader: {
      type: Boolean,
      required: false,
    },

    isSalarie: {
      type: Boolean,
      required: false,
    },

    readOnly: {
      type: Boolean,
      required: false,
    },

    showMissing: {
      type: Boolean,
      required: false,
    },

    ressourceHeader: {
      type: Object as PropType<MapRessource>,
      required: false,
    },

    fiche: {
      type: String,
      required: false,
    },
  },

  data() {
    return {
      toggleDialog: false,
      closeSection: false,
      localDialogWidth: undefined as number | string | undefined,
      panelVisible: false,
    };
  },

  mounted(): void {
    this.panelVisible = this.dialogOpen === true;
  },

  watch: {
    dialogOpen(value: boolean): void {
      if (value) {
        this.panelVisible = true;
        this.closeSection = false;
      }
    },
  },

  computed: {
    dialog: {
      get(): boolean {
        if (this.dialogOpen != this.toggleDialog) {
          this.toggleDialog = this.dialogOpen;
        }
        return this.toggleDialog;
      },
      set(value: boolean) {
        this.toggleDialog = value;
      },
    },

    currentProperties(): any {
      return {
        [this.section.type]: this.section.data,
        type: this.section.type,
        data: this.section.data,
        [this.section.propType]: this.section.propData,
        autoClear: this.section.autoClear,
        ressource: this.section.ressource,
        picto: this.section.picto,
        showButton: this.section.showButton,
        autoSave: this.section.autoSave,
        contextId: this.section.contextId,
        buttons: this.section.buttons,
        chat: this.section.chat,
        fullscreen: this.fullscreen,
        extended: true,
        closeSection: this.closeSection,
        dialog: true,
        moduleId: this.moduleId,
        isSalarie: this.isSalarie,
        readOnly: this.readOnly,
        showMissing: this.showMissing,
        ressourceHeader: this.ressourceHeader,
        fromSection: this.section.sectionName,
        section: this.section,
        owner: this.section.owner,
        adminInfo: this.section.adminInfo,
        fiche: this.fiche,
        requiredAttributs: this.section.requiredAttributs,
        isAbstract: true
      };
    },

    events(): Record<string, () => void> {
      const event: any = {};
      event['closeDialog'] = () => this.closeDialog();
      event['setSize'] = (fullscreen: boolean,midSize: boolean,width?: number | string) => this.setSize(fullscreen, midSize, width);
      event['itemCreated'] = () => this.itemCreated();
      event['saved'] = (value: any) => this.setSaved(value);
      event['cancelled'] = () => this.setCancelled();
      event['updated'] = (value: any) => this.setUpdated(value);
      event['setMinimum'] = (value: any) => this.setMinimum(value);
      event['status'] = (status: FieldStatus) => {this.statusChanged(status)};
      return event;
    },
  },

  methods: {
    closeDialog(): void {
      if (this.section.autoSave && !this.closeSection) {
        this.closeSection = true;
        return;
      }
      this.panelVisible = false;
    },

    afterPanelLeave(): void {
      const parent = this.$parent as any;
      if (parent?.route?.section) {
        Route.closeSection(parent.route.section);
      }
      this.toggleDialog = false;
      this.closeSection = false;
      this.$emit('closeDialog');
    },

    setSize(
        fullscreen: boolean,
        midSize: boolean,
        width?: number | string,
    ) {
      this.localDialogWidth = width;
      this.$emit('setSize', fullscreen, midSize, width);
    },

    setUpdated(value: any): void {
      this.$emit('updated', value);
    },

    setMinimum(value: any): void {
      this.$emit('setMinimum', value);
    },

    setCancelled(): void {
      this.$emit('cancelled');

      this.closeSection = false;
      this.panelVisible = false;
    },

    itemCreated(): void {
      this.$emit('itemCreated');
    },

    setSaved(value: any): void {
      this.$emit('saved', value);
    },

    statusChanged(status: FieldStatus): void {
      this.$emit('status', status);
    },
  },
});
</script>

<style scoped>

.section-panel-host {
  position: relative;
  width: 100vw;
  height: 100dvh;
  padding: 0;

  background: transparent !important;
  box-shadow: none !important;
  overflow: hidden;
}

.local-panel {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;

  width: 41.666667%;
  max-width: 100%;
  height: 100dvh;

  background: white;
  overflow: hidden;
}

.local-panel-card {
  width: 100%;
  height: 100%;
  border-radius: 0 !important;
  overflow: hidden;
}

.local-panel-content {
  height: 100%;
  overflow-y: auto;
}

.local-panel .v-card {
  box-shadow: none !important;
}

@media (max-width: 960px) {
  .local-panel {
    width: 100%;
  }
}
</style>